import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CdnBaseUrl } from '@deltek/specification-client/core/Utilities/urlUtils'
import { SpecTable } from '@deltek/specification-client/core/Widgets/SpecTable'
import { SpecTableTitleSettingsProps } from '@deltek/specification-client/core/Widgets/SpecTable/SpecTableTitleSettings'
import useSpecTableOptions from '@deltek/specification-client/core/Widgets/SpecTable/useSpecTableOptions'
import { alpha, Button, makeStyles, Theme, Typography } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditOutlined from '@material-ui/icons/EditOutlined'
import {
    CheckDuplicateFilesForImport,
    UploadFilesForImport,
} from 'api/graphQL/types/project.types'
import clsx from 'clsx'
import { format } from 'date-fns'
import {
    ImportUploadFileInfo,
    ProductTypeInfo,
    ProjectProductFamilyFileInput,
} from 'models/projectImport'
import {
    onEnterKeyDown,
    shortedImportTableData,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import { Row } from 'react-table'
import { WarningDialogType } from '../..'
import ImportProgress from '../ImportProgress/ImportProgress'
import ElementNameBlock from './ElementNameBlock'

const CdnClientBaseUrl = `${CdnBaseUrl}/knowledgepoint/client`

const useStyles = makeStyles((theme: Theme) => ({
    levelText: {
        ...theme.typography.caption,
        color: theme.palette.grey[700],
        textAlign: 'start',
    },

    fileNameText: {
        lineHeight: 1.5,
        maxWidth: 100,
    },
    '& .spectable-loading': {
        display: 'none',
    },
    actionIcon: {
        color: theme.palette.secondary.main,
        padding: theme.spacing(0.5),
    },
    deleteIcon: {
        color: '#EB5757',
        width: 24,
        height: 24,
        cursor: 'pointer',
    },
    previewIcon: {
        color: '#04325B',
        width: 24,
        height: 24,
        cursor: 'pointer',
    },
    setAssociation: {
        color: '#0000008A',
        width: 24,
        height: 24,
        cursor: 'pointer',
    },
    bpTableIcon: {
        width: pxToRem(40),
        height: pxToRem(40),
        cursor: 'pointer',
        color: theme.palette.common.white,
        position: 'relative',
    },
    causeWrapper: {
        borderRight: 0,
        borderLeft: 0,
        backgroundColor: theme.palette.common.white,

        border: '1px solid #DFDFDF',
        padding: theme.spacing(1),
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#1379B2',
        },
    },
    ptCount: {
        fontSize: pxToRem(24),
        color: '#828283',
        marginRight: (props: ImportPageTableStyleProps) =>
            props.isBPImport ? theme.spacing(2) : 0,
    },
    hiddenSpan: { visibility: 'hidden' },
    ptWrapper: {
        marginLeft: theme.spacing(2),
        borderRight: 0,
        backgroundColor: theme.palette.common.white,

        border: '1px solid #DFDFDF',
        padding: theme.spacing(1.25),
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#1379B2',
        },
    },

    associationWrapper: {
        backgroundColor: theme.palette.common.white,

        border: '1px solid #DFDFDF',
        padding: theme.spacing(1),
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#0B782D',
        },
    },
    associationFamily: {
        borderRight: 0,
        borderLeft: 0,
    },

    needsWrapper: {
        borderRight: 0,
        borderLeft: 0,
        backgroundColor: theme.palette.common.white,

        border: '1px solid #DFDFDF',
        padding: theme.spacing(1),
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#1379B2',
        },
    },

    removeWrapper: {
        borderLeft: 0,
        backgroundColor: theme.palette.common.white,
        border: '1px solid #DFDFDF',
        padding: theme.spacing(1),
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#B50808',
        },
        marginRight: theme.spacing(1),
    },
    causeIcon: {
        backgroundColor: '#1379B2',
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#1379B2',
        },
    },
    needsIcon: {
        backgroundColor: '#1379B2',
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#1379B2',
        },
    },

    iconAssociation: {
        backgroundColor: '#0B782D',
    },
    iconRemove: {
        backgroundColor: '#B50808',
        '& .MuiIconButton-root:hover': {
            backgroundColor: '#B50808',
        },
    },

    specTable: {
        '& .tr': {
            borderBottom: 'none',
            backgroundColor: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '#f2f4f6' : theme.palette.common.white,
        },
        '& .tr > .spectable-loading': {
            display: 'none',
        },
        '& .header': {
            display: (props: ImportPageTableStyleProps) =>
                props?.isBPImport && props?.isTableEmpty ? 'none' : 'block',
        },
        '&.horz-scroll': {
            overflow: 'hidden',
        },
        '& .td': {
            textAlign: 'center',
            margin: 'inherit !important',
            color: '#5E5F61',
            whiteSpace: 'normal !important',
        },
        '& .vertical-scroll': {
            border: 'none',
        },
    },
    addImage: {
        zIndex: 1,
    },
    imageArea: {
        zIndex: 2,
    },

    noData: {
        height: 700,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        borderTop: (props: ImportPageTableStyleProps) =>
            props?.isBPImport && props?.isTableEmpty
                ? 'none'
                : '1px solid #C9C9C9',
        flexDirection: 'column',
        paddingTop: (props: ImportPageTableStyleProps) =>
            props?.isBPImport && props?.isTableEmpty
                ? theme.spacing(30)
                : theme.spacing(20),
        '& div button.MuiButton-contained': {
            height: 30,
            width: 80,
            padding: 0,
        },
    },
    noDataText: {
        margin: theme.spacing(4, 4),
    },

    tableWhiteText: {
        color: '#FFF',
        fontWeight: 400,
        fontSize: pxToRem(12),
    },
    tableRowText: {
        color: '#304959',
        fontWeight: 400,
        width: '100%',
        whiteSpace: 'normal',
        fontSize: pxToRem(14),
    },
    tableRowButton: {
        padding: 0,
    },
    tableContainer: {
        '& .header > .tr .header-filter-container': {
            overflow: 'hidden',
        },
        '& .table-title-settings-container': {
            padding: 0,
            backgroundColor: theme.palette.common.white,
            '& .table-title-container': {
                ...theme.typography.h1,
                display: 'none !important',
                textAlign: 'center',
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
            },

            '& .clear-filter-container': {
                display: 'flex',
                alignItems: 'center',
            },
            '& button.clear-filter-button': {
                color: theme.palette.primary.light,
            },
            '& button.filter-option-button': {
                color: theme.palette.primary.main,
            },
        },
        '& .sticky.table': {
            overflowX: 'hidden !important',
            borderRadius: theme.spacing(0.5),
            width: '100% !important',
            marginBottom: 0,
            marginTop: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? theme.spacing(2) : 'auto',

            backgroundColor: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '#f2f4f6' : theme.palette.common.white,
        },
        '& .table>:not(caption)>*>*': {
            padding: 0,
        },
        '& .header': {
            '& .hoverTooltip': {
                textAlign: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? 'start' : 'center',
                wordBreak: 'break-word',
                whiteSpace: 'pre',
                textTransform: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? 'capitalize' : 'uppercase',
            },
            '& .sort-label-container': {
                display: 'none',
            },
            fontWeight: 500,
            '& > .tr': {
                '& .th:first-child > div, .th:first-child > div > div > div ': {
                    backgroundColor: (props: ImportPageTableStyleProps) =>
                        props?.isBPImport
                            ? '#343A3F'
                            : theme.palette.secondary.light,
                    color: theme.palette.common.white,
                    width: '100% !important',
                    textAlign: 'center',
                    fontSize: pxToRem(14),
                    '& .MuiInput-underline:after': {
                        borderBottomColor: theme.palette.common.white,
                    },
                    '& .MuiInput-underline:before': {
                        borderBottomColor: alpha(
                            theme.palette.common.white,
                            0.5
                        ),
                    },
                    '& .header-text-container': {
                        textAlign: (props: ImportPageTableStyleProps) =>
                            props?.isBPImport ? 'start' : 'center',
                        marginLeft: (props: ImportPageTableStyleProps) =>
                            props?.isBPImport ? theme.spacing(1.25) : 0,
                    },
                },
                '& .th': {
                    background: '#FFFDFA',
                },
                '& .th:not(:first-child) > div > div > div': {
                    fontSize: pxToRem(13),
                    color: '#402E32',
                    paddingLeft: '0px !important',
                    paddingRight: '0px !important',
                    '&:first-child': {
                        maxHeight: 60,
                        backgroundColor: (props: ImportPageTableStyleProps) =>
                            props?.isBPImport
                                ? '#f2f4f6'
                                : theme.palette.common.white,
                    },
                },
                '& input': {
                    paddingTop: theme.spacing(1.115),
                    paddingBottom: theme.spacing(0.5),
                    fontSize: pxToRem(12),
                },
                '& .header-filter-container': {
                    paddingBottom: theme.spacing(1.25),
                },
                '& .filter-select-state': {
                    fontSize: pxToRem(12),
                    paddingBottom: theme.spacing(0.25),
                },
            },
        },
        '& .body': {
            '& > .tr': {
                '& > .td': {
                    fontSize: pxToRem(12),
                    display: 'flex',
                    justifyContent: 'center',
                    whiteSpace: 'normal',
                    alignItems: 'center',
                },
                '& > .td:nth-child(2)': {
                    // This is to override the in-line styles
                    // generated by SpecTable.
                    textOverflow: 'unset !important',
                },

                '& > .td:not(:first-child)': {
                    margin: 0,
                    color: theme.palette.common.black,
                    display: 'flex !important',
                    padding: '0px !important',
                },
                '& > .td:first-child': {
                    backgroundColor: theme.palette.secondary.light,
                    margin: 'inherit',
                    borderBottom: '1px solid #B7A99A',
                    padding: theme.spacing(0.625),
                },
            },
            '& .MuiSkeleton-text': {
                transform: 'unset',
            },
        },
    },
    projectGroupActionIcon: {
        color: theme.palette.secondary.main,
        padding: theme.spacing(0),
        margin: 'auto',
    },
    tableViewContainer: {
        backgroundColor: '#ffffff',
        marginTop: 0,
        flex: 3,
        paddingLeft: (props: ImportPageTableStyleProps) =>
            props?.isBPImport ? theme.spacing(0) : theme.spacing(1.25),
        paddingTop: (props: ImportPageTableStyleProps) =>
            props?.isBPImport ? theme.spacing(6) : theme.spacing(1.25),
        [theme.breakpoints.between('lg', 'md')]: {
            flex: 2,
        },
        [theme.breakpoints.down('md')]: {
            flex: 2,
        },
        height: 'auto',
    },

    tableContainerData: {
        '& .body': {
            '& > .tr': {
                borderBottom: 'none',
                '&:first-child': {
                    borderTopColor: 'rgba(166, 137, 102, 0.25)',
                    paddingTop: 40,
                    width: '100% !important',
                },
            },
        },
    },
    tableContainerNoData: {
        display: 'none',
        '& .body': {
            '& > .tr': {
                borderBottom: 'none',
                '&:first-child': {
                    borderTop: 'none',
                    width: '100% !important',
                    display: 'none',
                },
            },
        },
    },
    specTableStyle: {
        '& .header-text-container': {
            zIndex: 5,
            maxWidth: '100%',
            flexBasis: '100%',
            flexGrow: 0,
        },
        '& .spectable-loading': {
            display: 'none',
        },
        '& .td': {
            fontSize: pxToRem(12),
            paddingLeft: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? 0 : 'auto',
            paddingRight: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? 0 : 'auto',
        },
        '& .tr': {
            justifyContent: 'space-between',
            textAlign: 'center',
            borderBottom: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? 'none' : '1px solid #C9C9C9',
            '&:first-child': {
                borderTop: 0,
            },
            '&:not(:first-child)': {
                backgroundColor: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? '#f2f4f6' : 'inherit',
            },
            '& .td:not(:first-child)': {
                backgroundColor: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? '#f2f4f6 !important' : 'inherit',
            },
        },
        '& .horz-scroll': {
            overflowX: 'hidden',
            width: '100% !important',
        },
        '& .header': {
            position: 'absolute',
            textAlign: 'center',
            '& .hoverTooltip': {
                textAlign: 'center',
                wordBreak: 'break-word',
                whiteSpace: 'pre',
                textOverflow: 'unset',
            },
        },
        '& .table-title-container': {
            fontSize: pxToRem(14),
            textTransform: 'uppercase',
            marginLeft: theme.spacing(-1.5),
            textAlign: 'center',
        },
        '& .MuiSvgIcon-root.arrow-down, .MuiSvgIcon-root.arrow-right': {
            color: (props: ImportPageTableStyleProps) =>
                props?.isBPImport
                    ? theme.palette.common.white
                    : 'rgba(0, 0, 0, 0.54)',
            marginTop: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? theme.spacing(0.5) : 'inherit',
            paddingTop: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? theme.spacing(0.5) : 'inherit',
        },
        '& .MuiGrid-justify-content-xs-space-between': {
            backgroundColor: '#fff',
            color: '#000',
            minWidth: 100,
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(0.5),
        },
        '& .MuiList-padding': {
            maxHeight: 300,
        },
        '& .table-title-settings-container': {
            display: 'none',
            '& .MuiDivider-vertical': {
                display: 'none',
            },
        },
        '& .light button.expand-all-button, .light button.collapse-all-button': {
            color: `${theme.palette.primary.main}`,
            border: `1px solid ${theme.palette.primary.main}`,
        },
        '& .MuiButton-containedPrimary': {
            backgroundColor: '#343334',
            color: theme.palette.common.white,
        },
        '& .view-as-table.active, .view-filter.active': {
            backgroundColor: theme.palette.primary.main,
        },
        '& .load-next-button': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '& .tr.grouped-row.expanded .td.grouped-cell': {
            backgroundColor: theme.palette.common.white,
            whiteSpace: 'normal',
        },
        '& .sticky .header': {
            zIndex: 5,
            backgroundColor: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '#f2f4f6' : theme.palette.common.white,
            '&:first-child': {
                width: '100% !important',
                display: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport && props?.isTableEmpty ? 'none' : 'block',
            },
            width: '100% !important',
            [theme.breakpoints.between('lg', 'md')]: {
                justifyContent: 'space-between',
                width: '100%',
            },
            [theme.breakpoints.down('md')]: {
                justifyContent: 'space-between',
                width: '100%',
            },
        },
    },

    progressBarContainer: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props.textWidth
                    ? props?.textWidth - (props.isBPImport ? 30 : 15)
                    : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            '&:hover': {
                backgroundColor: '#A78966',
                color: theme.palette.common.white,
            },
        },
    },
    progressBarContainerProductType: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props.textWidth ? props?.textWidth - 10 : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            '&:hover': {
                backgroundColor: '#A78966',
                color: theme.palette.common.white,
            },
        },
    },
    progressBarContainerCollapsed: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props.textWidth
                    ? props?.isBPImport
                        ? props?.textWidth - 30
                        : props?.textWidth - 20
                    : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            '&:hover': {
                backgroundColor: '#A78966',
                color: theme.palette.common.white,
            },
        },
    },
    progressBarContainerCollapsedFamily: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props.textWidth ? props?.textWidth - 20 : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            '&:hover': {
                backgroundColor: '#A78966',
                color: theme.palette.common.white,
            },
        },
    },

    progressBarContainerSelected: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props?.textWidth ? props?.textWidth - 10 : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            backgroundColor: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '#677B98' : '#D9F3F9',
            border: '1px solid #D9F3F9',
            '&:hover': {
                backgroundColor: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? '#677B98' : '#A78966',
                border: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport
                        ? '1px solid #677B98'
                        : '#1px solid #A78966',
                color: theme.palette.common.white,
            },
        },
    },
    progressBarContainerSelectedCollapsed: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props?.textWidth ? props?.textWidth - 30 : 290,
            textAlign: 'start',
        },
        cursor: 'pointer',
        '& .MuiPaper-root': {
            backgroundColor: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '#677B98' : '#D9F3F9',
            border: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? '1px solid #677B98' : '#1px solid #D9F3F9',
            '&:hover': {
                backgroundColor: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport ? '#677B98' : '#A78966',
                border: (props: ImportPageTableStyleProps) =>
                    props?.isBPImport
                        ? '1px solid #677B98'
                        : '#1px solid #A78966',
                color: theme.palette.common.white,
            },
        },
    },
    progressBarLoadingContainer: {
        '& .hoverTooltip': {
            width: (props: ImportPageTableStyleProps) =>
                props?.isBPImport ? 220 : 180,
            textAlign: 'start',
        },
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(0.5),
        width: (props: ImportPageTableStyleProps) =>
            props?.isBPImport ? 390 : 'auto',
        cursor: 'pointer',
        borderRadius: (props: ImportPageTableStyleProps) =>
            props?.isBPImport ? 3 : 4,
        backgroundColor: (props: ImportPageTableStyleProps) =>
            props?.isBPImport ? '#677B98' : theme.palette.common.white,
        marginLeft: (props: ImportPageTableStyleProps) =>
            props.isBPImport ? 20 : 0,
    },
    keyBoardArrowIcon: {
        display: 'block',
    },
    grayTextTable: {
        color: '#0098D6',
        cursor: 'pointer',
    },
    setAssociationButton: {
        borderRadius: 100,
        color: theme.palette.common.white,
        padding: theme.spacing(1.25),
        width: pxToRem(120),
        height: pxToRem(36),
        fontSize: pxToRem(14),
        fontWeight: 'normal',
        lineHeight: 1.5,
        background: '#A78966',
        '&:hover': {
            background: '#8F795F',
        },
    },
    selectedRow: {
        background: '#30B27E',
        color: theme.palette.common.white,
        border: '1px solid #1C9363',
        borderRadius: pxToRem(4),
        '&:hover': {
            background: '#38CE92',
            border: '1px solid #1C9363',
        },
    },
    dateText: {
        whiteSpace: 'pre-line',
        fontSize: pxToRem(12),
        alignItems: 'center',
        color: '#000',
    },
    columnScripts: {
        fontSize: pxToRem(12),
        position: 'absolute',
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(0.5),
        borderRadius: '100%',
        border: '1px solid #1379B2',
        width: 15,
        height: 15,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#1379B2',
        top: 0,
        left: 0,
    },
}))
interface ImportPageTableStyleProps {
    textWidth?: number
    actionBarCollapsed?: boolean
    isBPImport?: boolean
    isTableEmpty?: boolean
}
export interface ImportPageTableProps extends ImportPageTableStyleProps {
    showAddDialog: boolean
    addFileButton?: React.ReactNode
    handleChangeFileStatus: (fileData?: Partial<UploadFilesForImport>) => void
    handleClickSetAssociationButton?: (
        data: Partial<ImportUploadFileInfo>,
        productTypeName?: string
    ) => void
    instanceIds?: string[]
    filesData?: Partial<UploadFilesForImport>
    progressFilesData?: Partial<ProjectProductFamilyFileInput>[]
    loading?: boolean
    handleShowRemoveDialog?: (familyRow: Row) => void
    imageClickArea?: React.ReactNode
    handleShowIssueDialog: (fileIndex: number, type: WarningDialogType) => void
    handleDuplicateFileChangeFileStatus: (
        fileData?: Partial<CheckDuplicateFilesForImport>
    ) => void
    instanceId?: string
    totalRows?: number
    handleExpandRow?: (
        rowId: string,
        value: boolean,
        event:
            | React.MouseEvent<SVGSVGElement>
            | React.KeyboardEvent<SVGSVGElement>
    ) => void
    importPreviewTitle?: string
    handleSelectFileForPreview?: (
        fileKey?: string,
        selectedProduct?: string
    ) => void
    extractionProgressComponent?: React.ReactNode
    selectedFile?: Partial<ImportUploadFileInfo> | null
    showDuplicateDialog?: boolean
}
export interface ImportTableItems {
    name: string
    propertiesCount: number
    cautionCounts: string
    needs: string
    subRows: ProductTypeInfo[]
    productTypeCount: string
    removeData: string
    previewData: string
    imported: boolean
    expanded?: boolean
    dateTimeImported?: string
    associations?: CheckDuplicateFilesForImport[]
    fileKey?: string
}
const ImportPageTable: React.FC<ImportPageTableProps> = ({
    addFileButton,
    filesData,
    progressFilesData,
    handleChangeFileStatus,
    loading,
    imageClickArea,
    handleShowIssueDialog,
    handleShowRemoveDialog,
    handleDuplicateFileChangeFileStatus,
    instanceId,
    handleClickSetAssociationButton,
    handleExpandRow,
    importPreviewTitle,
    handleSelectFileForPreview,
    selectedFile,
    actionBarCollapsed,
    showDuplicateDialog,
    isBPImport,
}) => {
    const divContainer = React.useRef<HTMLDivElement>()
    const lastFocusedExpandCollapseIconRef = React.useRef<string | null>(null)
    const [textWidth, setTextWidth] = React.useState(0)
    const [progressPercent, setProgressPercent] = React.useState<number>(10)
    const [windowHeight, setWindowHeight] = React.useState<number>(
        window.innerHeight
    )

    const tableHeight = React.useMemo(() => {
        // (header) 60px + (bp actionbar) 64px = 124px
        const bpImportHeight = windowHeight - 124
        // (header) 60px + (collapsed actionbar) 40px + (padding-top) 10px = 110px
        const collapsedHeight = windowHeight - 110
        // (header) 60px + (uncollapsed actionbar) 90px + (padding-top) 10px = 160px
        const uncollapsedHeight = windowHeight - 160

        return isBPImport
            ? bpImportHeight
            : actionBarCollapsed
            ? collapsedHeight
            : uncollapsedHeight
    }, [windowHeight, isBPImport, actionBarCollapsed])
    const isLoadingActive = React.useCallback(
        (row: Row) =>
            loading &&
            progressFilesData?.filter(
                (item) => item?.fileName === row?.values?.expander
            )?.length !== 0,
        [progressFilesData, loading]
    )
    const getTextWidth = (width: number) => {
        setTextWidth(width)
    }

    const onAssociationClick = React.useCallback(
        (row: Row) => {
            const selectedFileIndex: number = Number(
                row.id.includes('.') ? row.id.split('.')?.[0] : row.id
            )
            const selectedPTIndex: number = Number(
                row.id.includes('.') ? row.id.split('.')?.[1] : -1
            )

            if (filesData?.fileInfo?.[selectedFileIndex]) {
                handleClickSetAssociationButton?.(
                    filesData?.fileInfo?.[selectedFileIndex],
                    selectedPTIndex >= 0
                        ? filesData?.fileInfo?.[selectedFileIndex]
                              ?.productTypeInfo?.[selectedPTIndex]?.name
                        : ''
                )
            }
        },
        [handleClickSetAssociationButton, filesData]
    )
    const formattedImportTableData:
        | Partial<ImportUploadFileInfo>[]
        | undefined = React.useMemo(() => {
        if (!filesData && progressFilesData) {
            return progressFilesData.map((item) => {
                return {
                    title: item.fileName,
                    propertiesCount: 0,
                    subRows: [],
                    productTypeInfo: [],
                    productTypeCount: '',
                    cautionCounts: '',
                    needs: '',
                    familyId: '000000',

                    processing: item.processing,
                    associations: item.associations,
                    parentAssemblyTreePaths: item?.parentAssemblyTreePaths,
                    imported: false,
                    fileKey: item?.key,
                }
            })
        } else if (filesData && progressFilesData) {
            const tempFileData = filesData?.fileInfo?.map((item, index) => {
                const productTypeInfo = [...(item?.productTypeInfo! ?? [])]
                return {
                    title: item.title ?? '',
                    propertiesCount: Number(item.propertiesCount) ?? '',
                    familyId: item?.familyId?.replaceAll(/['"]+/g, ''),
                    fileKey: item?.fileKey,
                    parentAssemblyTreePaths: item?.parentAssemblyTreePaths,
                    cautionCounts:
                        String(item?.paragraphWarnings?.length) ?? '',
                    needs:
                        String(
                            item?.paragraphErrors?.filter(
                                (item) =>
                                    !item?.children?.some((child) =>
                                        child.message.includes(
                                            'Duplicate Key Renamed'
                                        )
                                    )
                            )?.length
                        ) ?? '',
                    level: 'Family',
                    imported: item?.imported ?? false,
                    expanded: item.expanded,
                    dateTimeImported: item?.dateTimeImported
                        ? format(new Date(item.dateTimeImported), 'MM/dd/yyyy')
                        : format(new Date(), 'MM/dd/yyyy'),
                    associations: item.associations,
                    productTypeInfo: item?.productTypeInfo?.map((itemPT) => {
                        return {
                            title: itemPT.name
                                ? itemPT.name
                                : itemPT.title ?? '',
                            propertiesCount:
                                Number(itemPT.propertiesCount) ?? '',
                            subRows: undefined,
                            productTypeCount: '',
                            cautionCounts: '',
                            imported: false,
                            associations: itemPT.associations,
                            needs: '',
                            level: 'Product Type',
                            fileKey: item?.fileKey,
                        }
                    }),
                    subRows: productTypeInfo
                        ? productTypeInfo?.map((itemPT) => {
                              return {
                                  title: itemPT.name
                                      ? itemPT.name
                                      : itemPT.title ?? '',
                                  propertiesCount:
                                      Number(itemPT.propertiesCount) ?? '',
                                  subRows: undefined,
                                  productTypeCount: '',
                                  imported: false,
                                  cautionCounts: '',
                                  needs: '',
                                  associations: itemPT.associations,
                                  level: 'Product Type',
                                  fileKey: item?.fileKey,
                              }
                          })
                        : [],
                    productTypeCount:
                        String(item.productTypeCount) === ''
                            ? '0'
                            : String(item.productTypeCount),
                }
            })
            const tempProgressData = progressFilesData.map((item) => {
                return {
                    title: item.fileName,
                    propertiesCount: 0,
                    subRows: [],
                    productTypeInfo: [],
                    productTypeCount: '',
                    cautionCounts: '',
                    needs: '',
                    processing: item.processing,

                    imported: false,
                    fileKey: item?.key,
                }
            })
            return [...tempProgressData, ...(tempFileData ?? [])]
        } else if (filesData) {
            return filesData?.fileInfo?.map((item, index) => {
                const productTypeInfo = [...(item?.productTypeInfo! ?? [])]

                return {
                    title: item.name ? item.name : item.title ?? '',
                    propertiesCount: Number(item.propertiesCount) ?? '',
                    familyId: item?.familyId?.replaceAll(/['"]+/g, ''),
                    parentAssemblyTreePaths: item?.parentAssemblyTreePaths,
                    fileKey: item?.fileKey,
                    cautionCounts:
                        String(
                            item?.warnings?.filter(function (itemInner, pos) {
                                return item?.warnings?.indexOf(itemInner) == pos
                            })?.length
                        ) ?? '',
                    needs:
                        String(
                            item?.errors
                                ?.filter(function (itemInner, pos) {
                                    return (
                                        item?.errors?.indexOf(itemInner) == pos
                                    )
                                })
                                ?.filter(
                                    (item) =>
                                        !item?.includes('Duplicate Key Renamed')
                                )?.length
                        ) ?? '',
                    level: 'Family',
                    expanded: item.expanded,
                    associations: item.associations,
                    productTypeInfo: item?.productTypeInfo?.map((itemPT) => {
                        return {
                            title: itemPT.name
                                ? itemPT.name
                                : itemPT.title ?? '',
                            propertiesCount:
                                Number(itemPT.propertiesCount) ?? '',
                            subRows: undefined,
                            productTypeCount: '',
                            fileKey: item?.fileKey,
                            imported: false,
                            cautionCounts: '',
                            associations: itemPT.associations,
                            needs: '',
                            level: 'Product Type',
                        }
                    }),
                    dateTimeImported: item?.dateTimeImported
                        ? format(new Date(item.dateTimeImported), 'MM/dd/yyyy')
                        : format(new Date(), 'MM/dd/yyyy'),
                    imported: item?.imported ?? false,
                    subRows: productTypeInfo
                        ? productTypeInfo?.map((itemPT) => {
                              return {
                                  title: itemPT.name
                                      ? itemPT.name
                                      : itemPT.title ?? '',
                                  propertiesCount:
                                      Number(itemPT.propertiesCount) ?? '',
                                  subRows: undefined,
                                  productTypeCount: '',
                                  cautionCounts: '',
                                  imported: false,
                                  fileKey: item?.fileKey,

                                  needs: '',
                                  level: 'Product Type',
                                  associations: itemPT.associations,
                              }
                          })
                        : [],
                    productTypeCount:
                        String(item.productTypeCount) === ''
                            ? '0'
                            : String(item.productTypeCount),
                }
            })
        } else {
            return []
        }
    }, [progressFilesData, filesData, isBPImport])
    const classes = useStyles({
        textWidth: textWidth,
        isBPImport: isBPImport,
        isTableEmpty: formattedImportTableData?.length === 0,
    })
    const getLastElementHeight = React.useCallback(
        (totalRows: number) => {
            return tableHeight - (totalRows * (isBPImport ? 70 : 72) + 70)
        },
        [isBPImport, tableHeight]
    )
    const tableProp = React.useMemo(() => {
        const totalRows =
            formattedImportTableData && formattedImportTableData.length > 0
                ? formattedImportTableData
                      ?.map((item) =>
                          item.expanded ? Number(item.productTypeCount) : 0
                      )
                      ?.reduce((current, next) => {
                          return current + next
                      }) + formattedImportTableData?.length
                : 0
        const tableData =
            formattedImportTableData && formattedImportTableData.length !== 0
                ? totalRows >= (actionBarCollapsed ? 11 : 10)
                    ? shortedImportTableData(formattedImportTableData)
                    : shortedImportTableData(formattedImportTableData)?.concat([
                          {
                              title: '',
                              propertiesCount: 0,
                              subRows: [],
                              level: 'last',
                              productTypeInfo: [],
                              imported: true,
                              productTypeCount: '',
                          },
                      ])
                : []
        return {
            columns: [
                {
                    Header: 'Element Name',
                    id: 'expander', // It needs an ID
                    style: {
                        backgroundColor: isBPImport ? '#2F3843' : '#B7A99A',
                    },
                    // type 'Row' from 'react-table' always through an error if we try to access its child objecs

                    Cell: ({ row }: { row: any }) => {
                        return (
                            // Use Cell to render an expander for each row.
                            // We can use the getToggleRowExpandedProps prop-getter
                            // to build the expander.
                            row?.values?.expander?.length !== 0 ? (
                                <span
                                    className={clsx(
                                        row?.original?.level
                                            ? importPreviewTitle ===
                                                  row?.values?.expander &&
                                              row?.original?.fileKey ===
                                                  selectedFile?.fileKey
                                                ? row?.isExpanded &&
                                                  row?.original?.level ===
                                                      'Product Type'
                                                    ? classes.progressBarContainerSelected
                                                    : classes.progressBarContainerSelectedCollapsed
                                                : row?.isExpanded
                                                ? row?.values?.level ===
                                                  'Product Type'
                                                    ? classes.progressBarContainerProductType
                                                    : classes.progressBarContainer
                                                : row?.values?.level ===
                                                  'Family'
                                                ? classes.progressBarContainerCollapsedFamily
                                                : classes.progressBarContainerCollapsed
                                            : classes.progressBarLoadingContainer
                                    )}
                                >
                                    <ElementNameBlock
                                        row={row}
                                        isSelectedForPreview={
                                            importPreviewTitle ===
                                            row?.values?.expander
                                        }
                                        isBPImport={isBPImport}
                                        fileKey={row?.original?.fileKey}
                                        handleExpandRow={handleExpandRow}
                                        canExpand={row.canExpand}
                                        handleSelectFileForPreview={
                                            handleSelectFileForPreview
                                        }
                                        isFileImported={
                                            filesData?.fileInfo?.filter(
                                                (item) =>
                                                    item?.fileKey ===
                                                    row?.original?.fileKey
                                            )?.[0]?.imported
                                        }
                                        textWidth={textWidth}
                                        level={row?.original?.level}
                                        getTextWidth={getTextWidth}
                                        isLoadingActive={isLoadingActive(row)}
                                        lastFocusedExpandCollapseIconRef={
                                            lastFocusedExpandCollapseIconRef
                                        }
                                    />
                                    {loading &&
                                        progressFilesData?.filter(
                                            (item) =>
                                                item?.fileName ===
                                                row?.values?.expander
                                        )?.length !== 0 && (
                                            <>
                                                {
                                                    <ImportProgress
                                                        progressPercent={
                                                            progressPercent
                                                        }
                                                        isBPImport={isBPImport}
                                                    />
                                                }
                                            </>
                                        )}
                                </span>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            height: getLastElementHeight(
                                                totalRows
                                            ),
                                        }}
                                    />
                                </>
                            )
                        )
                    },

                    accessor: 'title',

                    minWidth: isBPImport ? 425 : 340,
                    width: textWidth + 15,
                    paddingRight: 8,
                },

                {
                    Header: 'Product\nTypes',
                    accessor: 'productTypeCount',
                    show: !isBPImport,
                    Cell: ({ row, value }: { row: any; value: number }) => {
                        if (
                            ((row.original.level === 'Product Type' ||
                                !value) &&
                                !isBPImport) ||
                            row?.original?.level === 'last' ||
                            isLoadingActive(row)
                        ) {
                            return <></>
                        } else {
                            return (
                                <div
                                    className={
                                        isBPImport ? classes.ptWrapper : ''
                                    }
                                    onClick={() =>
                                        handleSelectFileForPreview?.(
                                            row?.original?.fileKey,
                                            row?.id?.includes('.')
                                                ? row?.values?.expander
                                                : undefined
                                        )
                                    }
                                >
                                    {isBPImport ? (
                                        <Typography
                                            className={
                                                isBPImport
                                                    ? clsx(classes.ptCount, {
                                                          [classes.hiddenSpan]: row.id.includes(
                                                              '.'
                                                          ),
                                                      })
                                                    : ''
                                            }
                                        >
                                            {!value ? 0 : value}
                                        </Typography>
                                    ) : (
                                        value
                                    )}
                                </div>
                            )
                        }
                    },
                    minWidth: 100,
                    width: 100,
                },

                {
                    Header: 'Date',
                    accessor: 'dateTimeImported',
                    Cell: ({ row }: { row: any }) => {
                        return (
                            <div
                                onClick={() =>
                                    handleSelectFileForPreview?.(
                                        row?.original?.fileKey,
                                        row?.id?.includes('.')
                                            ? row?.value?.expander
                                            : undefined
                                    )
                                }
                                className={classes.dateText}
                            >
                                {row?.values?.needs?.length !== 0 &&
                                !row.id.includes('.') &&
                                row.values.dateTimeImported
                                    ? filesData?.fileInfo?.filter(
                                          (item, index) =>
                                              item?.fileKey ===
                                                  row?.original?.fileKey &&
                                              item?.imported
                                      )?.[0]?.imported
                                        ? `Imported on\n${row?.values?.dateTimeImported}`
                                        : `Draft Uploaded \non ${row?.values?.dateTimeImported}`
                                    : ''}
                            </div>
                        )
                    },
                    minWidth: 100,
                    width: 100,
                },

                {
                    Header: 'Caution',
                    accessor: 'cautionCounts',
                    // type 'Row' from 'react-table' always through an error if we try to access its child objecs
                    Cell: ({ row, value }: { row: any; value: number }) => {
                        return isBPImport &&
                            row?.original?.level !== 'last' &&
                            !isLoadingActive(row) ? (
                            <HoverTooltip
                                text={
                                    !row?.id?.includes('.')
                                        ? `Errors that aren't ideal, but don't inhibit import`
                                        : undefined
                                }
                            >
                                <div className={classes.causeWrapper}>
                                    <IconButton
                                        data-testid="causeIcon"
                                        className={clsx(
                                            classes.bpTableIcon,
                                            classes.causeIcon,
                                            {
                                                [classes.hiddenSpan]: row.id.includes(
                                                    '.'
                                                ),
                                            }
                                        )}
                                        onClick={() =>
                                            Number(value) !== 0 &&
                                            handleShowIssueDialog(
                                                row.index,
                                                'Caution' as WarningDialogType
                                            )
                                        }
                                    >
                                        <sup className={classes.columnScripts}>
                                            {value}
                                        </sup>
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </div>
                            </HoverTooltip>
                        ) : (
                            <div
                                title={`Errors that aren't ideal, but don't inhibit import`}
                                onClick={() =>
                                    Number(value) !== 0 &&
                                    handleShowIssueDialog(
                                        row.index,
                                        'Caution' as WarningDialogType
                                    )
                                }
                                onKeyDown={onEnterKeyDown(
                                    () =>
                                        Number(value) !== 0 &&
                                        handleShowIssueDialog(
                                            row.index,
                                            'Caution' as WarningDialogType
                                        )
                                )}
                                className={clsx(
                                    Number(value) !== 0 && classes.grayTextTable
                                )}
                                tabIndex={value > 0 ? 0 : undefined}
                            >
                                {value}
                            </div>
                        )
                    },
                    minWidth: isBPImport ? 160 : 100,
                    width: isBPImport ? 160 : 100,
                },
                {
                    Header: isBPImport ? 'Needs Attention' : 'Needs\nAttention',
                    // type 'Row' from 'react-table' always through an error if we try to access its child objecs

                    Cell: ({ row, value }: { row: any; value: number }) => {
                        return isBPImport &&
                            row?.original?.level !== 'last' &&
                            !isLoadingActive(row) ? (
                            <HoverTooltip
                                text={
                                    !row?.id?.includes('.')
                                        ? `Major errors that will prevent import`
                                        : undefined
                                }
                            >
                                <div className={classes.needsWrapper}>
                                    <IconButton
                                        data-testid="errorIcon"
                                        className={clsx(
                                            classes.bpTableIcon,
                                            classes.needsIcon,
                                            {
                                                [classes.hiddenSpan]: row.id.includes(
                                                    '.'
                                                ),
                                            }
                                        )}
                                        onClick={() =>
                                            Number(value) !== 0 &&
                                            handleShowIssueDialog(
                                                row?.index,
                                                'Needs Attention' as WarningDialogType
                                            )
                                        }
                                    >
                                        <sup className={classes.columnScripts}>
                                            {value}
                                        </sup>
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </div>
                            </HoverTooltip>
                        ) : (
                            <div
                                title={`Major errors that will prevent import`}
                                onClick={() =>
                                    Number(value) !== 0 &&
                                    handleShowIssueDialog(
                                        row?.index,
                                        'Needs Attention' as WarningDialogType
                                    )
                                }
                                onKeyDown={onEnterKeyDown(
                                    () =>
                                        Number(value) !== 0 &&
                                        handleShowIssueDialog(
                                            row?.index,
                                            'Needs Attention' as WarningDialogType
                                        )
                                )}
                                className={clsx(
                                    Number(value) !== 0 && classes.grayTextTable
                                )}
                                tabIndex={value > 0 ? 0 : undefined}
                            >
                                {value}
                            </div>
                        )
                    },
                    accessor: 'needs',
                    minWidth: isBPImport ? 160 : 110,
                    width: isBPImport ? 160 : 110,
                },
                {
                    Header: isBPImport
                        ? 'Set Associations'
                        : 'Set\nAssociations',
                    accessor: 'key',
                    Cell: ({ row }: { row: any }) => {
                        if (
                            row.original.title.length === 0 ||
                            !row.original.level
                        )
                            return <></>
                        else {
                            return isBPImport ? (
                                <div
                                    className={clsx(
                                        classes.associationWrapper,
                                        classes.associationFamily
                                    )}
                                >
                                    <IconButton
                                        data-testid="associationIcon"
                                        className={clsx(
                                            classes.bpTableIcon,
                                            classes.iconAssociation
                                        )}
                                        aria-label="Set Associations"
                                        onClick={() => onAssociationClick(row)}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </div>
                            ) : (!row.original.associations ||
                                  row.original.associations.length === 0) &&
                              (row?.id?.includes('.')
                                  ? true
                                  : row.original.parentAssemblyTreePaths
                                        ?.length === 0) ? (
                                <Button
                                    data-testid="setAssociationButton"
                                    color="primary"
                                    disableRipple
                                    className={clsx(
                                        'btnViewAllProducts',
                                        classes.setAssociationButton
                                    )}
                                    onClick={() => onAssociationClick(row)}
                                >
                                    Set Association
                                </Button>
                            ) : (
                                <IconButton
                                    data-testid="associationIcon"
                                    className={classes.setAssociation}
                                    aria-label="Set Associations"
                                    onClick={() => onAssociationClick(row)}
                                >
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                            )
                        }
                    },
                    minWidth: 130,
                    width: 130,
                },
                {
                    Header: 'Remove',
                    accessor: 'removeData',
                    // type 'Row' from 'react-table' always through an error if we try to access its child objecs

                    Cell: ({ row, value }: { row: any; value: string }) => {
                        return ((!row.id.includes('.') &&
                            row?.original?.level &&
                            !isBPImport) ||
                            (isBPImport && row?.original?.level)) &&
                            row?.original?.level !== 'last' ? (
                            <div
                                className={
                                    isBPImport ? classes.removeWrapper : ''
                                }
                            >
                                <IconButton
                                    data-testid="deletIconImportFile"
                                    className={
                                        isBPImport
                                            ? clsx(
                                                  classes.iconRemove,
                                                  classes.bpTableIcon,
                                                  {
                                                      [classes.hiddenSpan]:
                                                          row.id.includes(
                                                              '.'
                                                          ) && !!isBPImport,
                                                  }
                                              )
                                            : classes.deleteIcon
                                    }
                                    aria-label="Remove button"
                                    onClick={() =>
                                        handleShowRemoveDialog?.(row)
                                    }
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </div>
                        ) : (
                            <></>
                        )
                    },
                    minWidth: isBPImport ? 160 : 100,
                    width: isBPImport ? 160 : 100,
                },
            ],
            data: tableData,
            hiddenColumns: isBPImport ? ['dateTimeImported'] : [''],
            height: tableHeight,
            getSubRows: (row: Partial<ImportUploadFileInfo>) =>
                row.subRows || [],

            fetchNextDataLoading: false,
            sortBy: [{ id: 'title', desc: false }],
            divContainerRef: divContainer,
            fetchData: () => 'a',
            fetchDataLoading: false,
            fetchDataNextLoading: false,
            totalItems: 100,
            noDataComponent:
                formattedImportTableData?.length === 0 && !loading ? (
                    <div
                        className={classes.noData}
                        role="row"
                        data-testid="import-noData"
                    >
                        <div role="cell">
                            <GridItem>
                                <img
                                    src={`${CdnClientBaseUrl}${
                                        isBPImport
                                            ? '/uploadDocument'
                                            : '/emptyImport'
                                    }.png`}
                                    alt="Get Started!"
                                    height={230}
                                    width="auto"
                                    useMap="#emptyImportMap"
                                    className={classes.addImage}
                                />
                                {imageClickArea}
                            </GridItem>
                            <span className={classes.noDataText}>
                                No Files Have Been Imported
                            </span>

                            {addFileButton}
                        </div>
                    </div>
                ) : (
                    <></>
                ),
            specTableTitleSettings: {
                showFiltersRow: false,
                showTableSettings: false,
                tableViewActive: false,
                theme: 'light',

                tableSettings: {
                    initialPinningProps: {
                        firstColumnPinned: false,
                        lastColumnPinned: false,
                    },
                },
            } as SpecTableTitleSettingsProps<ImportUploadFileInfo>,
        }
    }, [
        formattedImportTableData,
        instanceId,
        progressFilesData,
        importPreviewTitle,
        selectedFile,
        progressPercent,
        loading,
        actionBarCollapsed,
        handleSelectFileForPreview,
        isBPImport,
        tableHeight,
        isLoadingActive,
    ])
    const importTableProps = useSpecTableOptions<any>(tableProp)

    React.useEffect(() => {
        // Recomputes the windowHeight used for computing the tableHeight dynamically.
        const updateWindowDimensions = () => {
            setWindowHeight(window.innerHeight)
        }

        window.addEventListener('resize', updateWindowDimensions)

        return () =>
            window.removeEventListener('resize', updateWindowDimensions)
    }, [])

    React.useEffect(() => {
        const progressInterval = setInterval(() => {
            if (loading) {
                if (progressPercent < 90) {
                    setProgressPercent(progressPercent + 10)
                } else if (progressPercent >= 90 && progressPercent < 100) {
                    setProgressPercent(progressPercent + 2)
                }
            }
        }, 1000)
        return () => {
            clearInterval(progressInterval)
        }
    }, [loading, progressPercent, setProgressPercent, showDuplicateDialog])
    React.useEffect(() => {
        if (!loading) {
            setProgressPercent(10)
        }
    }, [loading])
    return (
        <GridContainer
            className={clsx(classes.tableViewContainer)}
            data-testid="projectTeamGrid"
        >
            <GridItem
                xs={12}
                ref={divContainer}
                className={clsx(classes.tableContainer, classes.specTableStyle)}
            >
                <SpecTable isImportTable={true} {...importTableProps} />
            </GridItem>
        </GridContainer>
    )
}

export default ImportPageTable
